<template>
  <div class="calc-box">
    <el-form :model="form" :rules="rules" ref="form" label-width="0" class="calc-form" size="small">
      <!-- ============ 路由 ============ -->
      <div class="calc-card calc-card-route">
        <div class="calc-card-head">
          <span class="calc-card-title">
            <i class="el-icon-position card-ico"></i>
            {{ $t('components.calc.shipFrom') }}
          </span>
          <div class="card-head-right">
            <el-checkbox v-model="form.is_default" :true-label="1" :false-label="0" name="is_default" class="resi-check">
              {{ $t('components.calc.isResidential') }}
            </el-checkbox>
            <el-radio-group v-model="form.pa_unit" size="mini" class="unit-toggle">
              <el-radio-button :label="$t('components.calc.lbInch')"></el-radio-button>
              <el-radio-button :label="$t('components.calc.kgCm')"></el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <div class="route-row">
          <!-- 起 -->
          <div class="route-side route-from">
            <div class="route-side-tag">FROM</div>
            <el-form-item prop="start_country" class="route-item route-country">
              <el-select v-model="form.start_country" :placeholder="$t('components.calc.from')">
                <el-option v-for="(item, index) in countryList" :key="index" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item prop="start_zip_code" class="route-item route-zip">
              <el-input v-model="form.start_zip_code" :placeholder="$t('components.calc.zipCode')" maxlength="10" @blur="getCityInfo(1)">
                <i slot="prefix" class="el-input__icon el-icon-location-outline"></i>
              </el-input>
            </el-form-item>
          </div>

          <!-- 中间箭头 -->
          <div class="route-arrow" aria-hidden="true">
            <span class="route-arrow-line"></span>
            <span class="route-arrow-dot"><i class="el-icon-right"></i></span>
            <span class="route-arrow-line"></span>
          </div>

          <!-- 收 -->
          <div class="route-side route-to">
            <div class="route-side-tag">TO</div>
            <el-form-item prop="destination_country" class="route-item route-country">
              <el-select v-model="form.destination_country" :placeholder="$t('components.calc.to')">
                <el-option v-for="(item, index) in countryList" :key="index" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item prop="destination_zip_code" class="route-item route-zip">
              <el-input v-model="form.destination_zip_code" :placeholder="$t('components.calc.zipCode')" maxlength="10" @blur="getCityInfo(2)">
                <i slot="prefix" class="el-input__icon el-icon-location-outline"></i>
              </el-input>
            </el-form-item>
          </div>
        </div>
      </div>

      <!-- ============ 包裹信息 ============ -->
      <div class="calc-card calc-card-pack">
        <div class="calc-card-head">
          <span class="calc-card-title">
            <i class="el-icon-box card-ico"></i>
            {{ $t('components.calc.packageInfo') }}
          </span>
        </div>

        <div class="pack-row">
          <!-- 重量胶囊 -->
          <div class="pack-group pack-weight">
            <span class="pack-group-label">{{ $t('components.calc.weight') }}</span>
            <el-form-item prop="pa_lb" class="pack-input">
              <el-input v-model="form.pa_lb" type="number" :controls="false">
                <span slot="suffix" class="unit-suffix">{{ weightUnit }}</span>
              </el-input>
            </el-form-item>
            <el-form-item prop="oz" class="pack-input pack-oz" v-if="form.pa_unit === $t('components.calc.lbInch')">
              <el-input v-model="form.oz" type="number" :controls="false">
                <span slot="suffix" class="unit-suffix">{{ $t('components.calc.oz') }}</span>
              </el-input>
            </el-form-item>
          </div>

          <!-- 尺寸胶囊 -->
          <div class="pack-group pack-dims">
            <span class="pack-group-label">{{ $t('components.calc.length') }} × {{ $t('components.calc.width') }} × {{ $t('components.calc.height') }}</span>
            <el-form-item prop="pa_length" class="pack-input">
              <el-input v-model="form.pa_length" type="number" :controls="false" :placeholder="$t('components.calc.length')">
                <span slot="suffix" class="unit-suffix">{{ lengthUnit }}</span>
              </el-input>
            </el-form-item>
            <span class="dim-x">×</span>
            <el-form-item prop="pa_width" class="pack-input">
              <el-input v-model="form.pa_width" type="number" :controls="false" :placeholder="$t('components.calc.width')">
                <span slot="suffix" class="unit-suffix">{{ lengthUnit }}</span>
              </el-input>
            </el-form-item>
            <span class="dim-x">×</span>
            <el-form-item prop="pa_height" class="pack-input">
              <el-input v-model="form.pa_height" type="number" :controls="false" :placeholder="$t('components.calc.height')">
                <span slot="suffix" class="unit-suffix">{{ lengthUnit }}</span>
              </el-input>
            </el-form-item>
          </div>

          <!-- 件数 -->
          <div class="pack-group pack-qty">
            <span class="pack-group-mul">×</span>
            <el-form-item prop="pack_num" class="pack-input">
              <el-input v-model="form.pack_num" type="number" :controls="false" :placeholder="$t('components.calc.parcelNum')">
                <span slot="suffix" class="unit-suffix">PCS</span>
              </el-input>
            </el-form-item>
          </div>
        </div>
      </div>

      <!-- ============ 操作 ============ -->
      <div class="calc-actions">
        <el-radio-group v-model="search.pa_unit" size="small" @change="changeSearch" class="carrier-filter">
          <el-radio-button :label="$t('components.calc.all')"></el-radio-button>
          <el-radio-button label="FedEx"></el-radio-button>
          <el-radio-button label="UPS"></el-radio-button>
        </el-radio-group>
        <div class="action-buttons">
          <el-button size="small" @click="resetForm('form')" icon="el-icon-refresh-left" plain>{{ $t('components.calc.reset') }}</el-button>
          <el-button size="small" type="primary" @click="submitForm('form')" :loading="calcLoading" icon="el-icon-search">
            {{ $t('components.calc.getRates') }}
          </el-button>
        </div>
      </div>
    </el-form>

    <!-- ============ 结果表 ============ -->
    <el-table
      :data="dataList"
      border
      size="small"
      height="500"
      class="rate-table"
      :empty-text="$t('components.calc.emptyText')"
    >
      <el-table-column prop="service" :label="$t('components.calc.service')" min-width="280" show-overflow-tooltip>
        <template slot-scope="scope">
          <span class="service-pill" :class="carrierBadge(scope.row)">
            <i class="el-icon-truck"></i>
            {{ scope.row.service }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="name" :label="$t('components.calc.packageType')" width="180" show-overflow-tooltip>
        <template slot-scope="scope">
          <span class="package-name">{{ scope.row.name }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="money" :label="$t('components.calc.rate')" min-width="160">
        <template slot-scope="scope">
          <span class="rate-cell">$ {{ scope.row.money }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('components.calc.action')" width="120" align="center">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="handleCalc(scope.row)" plain class="use-btn">
            {{ $t('components.calc.useThis') }}
            <i class="el-icon-arrow-right"></i>
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagization-box">
      <CustomPagization :background="true" :currentPage="current" :total="total" :pageSize="size" @current-change="currentChange" />
    </div>
  </div>
</template>

<script>
import { orderw } from "@/api/calc";
import { cityInfo } from "@/api/order"
import { calcCountry } from "@/api/common";
import { transformCountryCity } from "@/common/common.js";
export default {
  data() {
    return {
      calcLoading: false,
      form: {
        start_zip_code: "", //发件人邮编
        destination_zip_code: "", //收件人邮编
        start_country: "United States", //发件人国家
        destination_country: "United States", //收件人国家
        pa_unit: this.$t('components.calc.lbInch'), //单位 1:lb2:kg
        pa_lb: 0, //重量
        pa_length: 0, //长
        pa_width: 0, //宽
        pa_height: 0, //高
        pack_num: 1, //包裹数量
        oz: 0,
      },
      uform: {
        send_data_province: '',
        send_data_city: '',
        receive_data_province: '',
        receive_data_city: ''
      },
      current: 1,
      size: 20,
      total: 1,
      dataList: [],
      AllList: [],
      search: {
        pa_unit: this.$t('components.calc.all')
      },
      rules: {
        start_country: [{ required: true, message: this.$t('components.calc.pleaseSelectShipCountry'), trigger: "change" }],
        start_zip_code: [{ required: true, message: this.$t('components.calc.pleaseEnterShipZipCode'), trigger: "blur" }],
        destination_country: [{ required: true, message: this.$t('components.calc.pleaseSelectReceiveCountry'), trigger: "blur" }],
        destination_zip_code: [{ required: true, message: this.$t('components.calc.pleaseEnterReceiveZipCode'), trigger: "change" }],
        pa_lb: [{ required: true, message: this.$t('components.calc.pleaseEnterWeight'), trigger: "blur" }],
        pa_length: [{ required: true, message: this.$t('components.calc.pleaseEnterLength'), trigger: "blur" }],
        pa_width: [{ required: true, message: this.$t('components.calc.pleaseEnterWidth'), trigger: "blur" }],
        pa_height: [{ required: true, message: this.$t('components.calc.pleaseEnterHeight'), trigger: "blur" }],
      },
      countryList: [], // 国家-城市
    };
  },
  computed: {
    weightUnit() {
      return this.form.pa_unit === this.$t('components.calc.lbInch') ? 'lb' : 'kg';
    },
    lengthUnit() {
      return this.form.pa_unit === this.$t('components.calc.lbInch') ? 'in' : 'cm';
    }
  },
  mounted() {
    this.getCity();
  },
  methods: {
    getCityInfo(num){
      let zcode = num == 1 ? this.form.start_zip_code : this.form.destination_zip_code
      cityInfo({
        zcode
      }).then(res => {
        if(res.code == 1){
          if(num == 1){
            this.uform.send_data_province = res.data.province
            this.uform.send_data_city = res.data.city
          } else if(num == 2){
            this.uform.receive_data_province = res.data.province
            this.uform.receive_data_city = res.data.city
          } 
        }
      })
    },
    // 获取城市
    getCity() {
      calcCountry().then((res) => {
        this.countryList = transformCountryCity(res.data);
      });
    },
    // 给结果行返回承运商小徽标 class
    carrierBadge(row) {
      var name = (row && (row.name || row.service) || '').toString().toLowerCase();
      if (name.indexOf('fedex') !== -1 || name.indexOf('zd-fed') !== -1) return 'is-fedex';
      if (name.indexOf('ups') !== -1) return 'is-ups';
      if (name.indexOf('usps') !== -1) return 'is-usps';
      return '';
    },
    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          let form = {
            ...this.form,
            start_country: this.form.start_country == 'United States' ? 'USA' : (this.form.start_country == 'CHINA' ? 'China' : this.form.start_country),
            destination_country: this.form.destination_country == 'United States' ? 'USA' : (this.form.destination_country == 'CHINA' ? 'China' : this.form.destination_country),
            pa_unit: this.form.pa_unit == this.$t('components.calc.lbInch') ? 1 : 2
          }
          this.calcLoading = true
          orderw({
            user_code: JSON.parse(window.localStorage.getItem("userInfo1")).user_code,
            ...form,
          })
            .then((res) => {
              this.calcLoading = false
              if (res.code == 1) {
                this.$message.success(res.msg);
                this.dataList = res.data.success;
                this.AllList = res.data.success
              } else {
                this.$message.error(res.msg);
              }
            })
            .catch((err) => {
              this.calcLoading = false
              this.$message.error(err.msg);
            });
        } else {
          return false;
        }
      });
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.form = {
        user_code: "1", //用户编码
        start_zip_code: "", //发件人邮编
        destination_zip_code: "", //收件人邮编
        start_country: "United States", //发件人国家
        destination_country: "United States", //收件人国家
        pa_unit: this.$t('components.calc.lbInch'), //单位 1:lb2:kg
        pa_lb: 0, //重量
        pa_length: 0, //长
        pa_width: 0, //宽
        pa_height: 0, //高
        pack_num: 1, //包裹数量
        oz: 0,
      };
      this.dataList = []
      this.AllList = []
      this.search.pa_unit = this.$t('components.calc.all')
    },
    // 页数变化
    currentChange(current) {
      this.current = current;
      this.getAddress();
    },
    // 筛选列表
    changeSearch(val){
      if(val == this.$t('components.calc.all')){
        this.dataList = this.AllList
      }else{
        this.dataList = this.AllList.filter(item => item.name.toLowerCase().includes(val.slice(0,3).toLowerCase()));
      }
    },
    // 跳转创建快递面单页面
    handleCalc(row){
      this.$router.push({
        path: '/bill/place',
        query: {
          rates: JSON.stringify(row),
          form: JSON.stringify({...this.form,...this.uform})
        }
      })
      this.$emit('closeCalc')
    }
  },
};
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$primary-soft: #eff6ff;
$ink-900: #0f172a;
$ink-700: #334155;
$ink-500: #64748b;
$ink-400: #94a3b8;
$ink-200: #e2e8f0;
$ink-100: #f1f5f9;
$bg-card: #ffffff;
$bg-soft: #f8fafc;
$danger: #ef4444;
$accent-fedex: #4d148c;
$accent-fedex-2: #ff6600;
$accent-ups: #5a3818;
$accent-ups-2: #ffb500;

.calc-box {
  min-width: 1180px;
  color: $ink-900;
  font-size: 13px;
}

.calc-form { margin: 0; }

/* ===== 卡片化容器 ===== */
.calc-card {
  background: $bg-card;
  border: 1px solid $ink-200;
  border-radius: 12px;
  padding: 14px 16px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06); }
}

.calc-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.calc-card-title {
  font-size: 13px;
  font-weight: 600;
  color: $ink-700;
  letter-spacing: 0.2px;
  display: inline-flex;
  align-items: center;
}

.card-ico {
  display: inline-flex;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: $primary-soft;
  color: $primary;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: 8px;
}

.card-head-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.resi-check {
  color: $ink-500;
  ::v-deep .el-checkbox__label {
    font-size: 12px;
    color: $ink-500;
  }
}

.unit-toggle {
  ::v-deep .el-radio-button__inner {
    padding: 6px 12px;
    font-size: 12px;
    border-color: $ink-200;
  }
  ::v-deep .el-radio-button:first-child .el-radio-button__inner {
    border-radius: 6px 0 0 6px;
  }
  ::v-deep .el-radio-button:last-child .el-radio-button__inner {
    border-radius: 0 6px 6px 0;
  }
  ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background: $primary;
    border-color: $primary;
    box-shadow: -1px 0 0 0 $primary;
  }
}

/* ===== 路由行 ===== */
.route-row {
  display: flex;
  align-items: stretch;
  gap: 14px;
}

.route-side {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: $bg-soft;
  border-radius: 10px;
  border: 1px solid $ink-200;
  position: relative;
  min-width: 0;
}

.route-side-tag {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  padding: 4px 10px;
  border-radius: 6px;
  color: #fff;
  background: $primary;
  display: inline-flex;
  align-items: center;
  height: 32px;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.25);
}
.route-to .route-side-tag {
  background: #16a34a;
  box-shadow: 0 1px 2px rgba(22, 163, 74, 0.25);
}

.route-item { margin-bottom: 0 !important; flex: 1; min-width: 0; }
.route-country { flex: 1.4; }
.route-zip { flex: 1; }

.route-arrow {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 56px;
}
.route-arrow-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, transparent, $ink-200, transparent);
}
.route-arrow-dot {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $primary;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
}

/* ===== 包裹信息行 ===== */
.calc-card-pack {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
}

.pack-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.pack-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 6px;
  background: $bg-soft;
  border: 1px solid $ink-200;
  border-radius: 10px;
  position: relative;
}

.pack-group-label {
  flex-shrink: 0;
  font-size: 11px;
  color: $primary;
  font-weight: 600;
  letter-spacing: 0.4px;
  white-space: nowrap;
  background: $primary-soft;
  padding: 5px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  height: 30px;
  box-sizing: border-box;
}

.pack-input {
  margin-bottom: 0 !important;
  width: 110px;
}
.pack-oz { width: 90px; }

.pack-weight .pack-group-label {
  background: rgba(37, 99, 235, 0.08);
}
.pack-dims .dim-x {
  color: $ink-400;
  font-size: 14px;
  font-weight: 700;
  user-select: none;
  margin: 0 -2px;
}

.pack-qty {
  margin-left: auto;
  background: rgba(37, 99, 235, 0.05);
  border-color: rgba(37, 99, 235, 0.2);
  border-style: dashed;
}
.pack-group-mul {
  font-size: 16px;
  color: $primary;
  font-weight: 700;
  margin-right: 2px;
}
.pack-qty .pack-input { width: 100px; }

/* 单位 suffix */
.unit-suffix {
  font-size: 11px;
  color: $ink-400;
  padding-right: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  font-variant: small-caps;
}

/* element-ui 输入框统一打磨 */
::v-deep .pack-input .el-input__inner,
::v-deep .route-zip .el-input__inner {
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
}
::v-deep .pack-input .el-input__inner {
  padding-right: 36px;
}

/* ===== 操作行 ===== */
.calc-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 14px 0 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.carrier-filter {
  ::v-deep .el-radio-button__inner {
    padding: 7px 18px;
    font-size: 13px;
    font-weight: 500;
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
}
.action-buttons .el-button {
  min-width: 96px;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 500;
}

/* ===== 结果表 ===== */
.rate-table {
  margin-top: 4px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid $ink-200;
}

::v-deep .rate-table {
  thead th {
    background: $ink-100 !important;
    color: $ink-700;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.3px;
  }
  .el-table__row td {
    padding: 10px 0;
  }
  .el-table__row:hover td {
    background-color: $primary-soft !important;
  }
  .el-table__empty-block {
    padding: 60px 0;
  }
  .el-table__empty-text {
    color: $ink-400;
    font-size: 13px;
  }
}

.service-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: $ink-100;
  color: $ink-700;
  font-size: 12px;
  font-weight: 500;
  i { font-size: 12px; }
  &.is-fedex {
    background: rgba(77, 20, 140, 0.08);
    color: $accent-fedex;
    i { color: $accent-fedex-2; }
  }
  &.is-ups {
    background: rgba(255, 181, 0, 0.18);
    color: $accent-ups;
    i { color: $accent-ups; }
  }
  &.is-usps {
    background: rgba(0, 78, 162, 0.10);
    color: #004ea2;
  }
}

.package-name {
  color: $ink-500;
  font-size: 12px;
}

.rate-cell {
  font-weight: 700;
  color: $danger;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.use-btn {
  border-radius: 6px;
  padding: 6px 14px;
}

.pagization-box {
  overflow: hidden;
  margin-top: 10px;
  .el-pagination { float: right; }
}

/* 数字输入框去掉箭头 */
::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button { -webkit-appearance: none !important; }
::v-deep input[type="number"] { -moz-appearance: textfield; }

/* ===== 窄屏适配 ===== */
@media (max-width: 1279px) {
  .calc-box { min-width: 0; }
  .route-row { flex-direction: column; }
  .route-arrow { display: none; }
  .pack-row { gap: 10px; }
  .pack-qty { margin-left: 0; }
}
</style>
